import { defineComponent as _defineComponent } from 'vue'
import { withDefaults, computed } from "vue";
import DatePicker from "vue2-datepicker";

import ButtonCommon from "../buttons/ButtonCommon.vue";

import "vue2-datepicker/index.css";

/**
 * Types
 */

type DateInput = Date | null | [Date, Date] | [null, null];

type Props = {
  clearText: string;
  disabledDate?: (date: Date, currentValue: Date[]) => boolean;
  disabledTime?: (date: Date) => boolean;
  doneText: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  isRange?: boolean;
  monthNames: Array<string>;
  placeholder: string;
  shouldAppendToBody?: boolean;
  sidebarOptions: Array<string>;
  type?: "date" | "datetime";
  value?: DateInput;
  weekDayNames: Array<string>;
};

/**
 * Utils
 */


export default /*#__PURE__*/_defineComponent({
  __name: 'DateTimePicker',
  props: {
    clearText: null,
    disabledDate: { type: Function, default: undefined },
    disabledTime: { type: Function, default: undefined },
    doneText: null,
    isClearable: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
    isFullWidth: { type: Boolean, default: false },
    isRange: { type: Boolean },
    monthNames: null,
    placeholder: null,
    shouldAppendToBody: { type: Boolean, default: false },
    sidebarOptions: null,
    type: { default: "datetime" },
    value: { default: null },
    weekDayNames: null
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ({
  (e: "input", value: DateInput): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { clearText: string, disabledDate: (date: Date, currentValue: Date[]) => boolean, disabledTime: (date: Date) => boolean, doneText: string, isClearable: boolean, isDisabled: boolean, isFullWidth: boolean, isRange?: boolean, monthNames: Array<string>, placeholder: string, shouldAppendToBody: boolean, sidebarOptions: Array<string>, type: "date" | "datetime", value: DateInput, weekDayNames: Array<string> };

const getCurrentDayDateRange = () => {
  const currentDate = new Date();

  const currentDayStart = new Date(currentDate);
  currentDayStart.setHours(0, 0, 0, 0);

  const currentDayEnd = new Date(currentDate);

  return [currentDayStart, currentDayEnd];
};

const getYesterdayDateRange = () => {
  const currentDate = new Date();

  const yesterdayStartDate = new Date(currentDate);
  yesterdayStartDate.setDate(yesterdayStartDate.getDate() - 1);
  yesterdayStartDate.setHours(0, 0, 0, 0);

  const yesterdayEndDate = new Date(currentDate);
  yesterdayEndDate.setDate(yesterdayEndDate.getDate() - 1);
  yesterdayEndDate.setHours(23, 59, 59, 999);

  return [yesterdayStartDate, yesterdayEndDate];
};

const getCurrentWeekDateRange = () => {
  const currentDate = new Date();
  const currentWeekEndDate = new Date(currentDate);
  const currentWeekStartDate = new Date(currentDate);

  // 0 = sun, 1 = mon, ..., 6 = sat
  const currentDayOfWeek = currentDate.getDay();

  currentWeekStartDate.setDate(
    currentWeekStartDate.getDate() -
      currentDayOfWeek +
      (currentDayOfWeek === 0 ? -6 : 1)
  );
  currentWeekStartDate.setHours(0, 0, 0, 0);

  return [currentWeekStartDate, currentWeekEndDate];
};

const getLastWeekDateRange = () => {
  const currentDate = new Date();
  const lastWeekEndDate = new Date(currentDate);
  const lastWeekStartDate = new Date(currentDate);

  // 0 = sun, 1 = mon, ..., 6 = sat
  const currentDayOfWeek = currentDate.getDay();

  lastWeekStartDate.setDate(
    lastWeekStartDate.getDate() -
      currentDayOfWeek +
      (currentDayOfWeek === 0 ? -6 : 1) -
      7
  );
  lastWeekStartDate.setHours(0, 0, 0, 0);

  lastWeekEndDate.setDate(
    lastWeekEndDate.getDate() -
      currentDayOfWeek +
      (currentDayOfWeek === 0 ? -6 : 1) -
      1
  );
  lastWeekEndDate.setHours(23, 59, 59, 999);

  return [lastWeekStartDate, lastWeekEndDate];
};

const getLast2WeeksDateRange = () => {
  const currentDate = new Date();
  const lastWeekEndDate = new Date(currentDate);
  const lastWeekStartDate = new Date(currentDate);

  // 0 = sun, 1 = mon, ..., 6 = sat
  const currentDayOfWeek = lastWeekStartDate.getDay();

  lastWeekStartDate.setDate(
    lastWeekStartDate.getDate() -
      currentDayOfWeek +
      (currentDayOfWeek === 0 ? -6 : 1) -
      14
  );
  lastWeekStartDate.setHours(0, 0, 0, 0);

  lastWeekEndDate.setDate(
    lastWeekEndDate.getDate() -
      currentDayOfWeek +
      (currentDayOfWeek === 0 ? -6 : 1) -
      1
  );
  lastWeekEndDate.setHours(23, 59, 59, 999);

  return [lastWeekStartDate, lastWeekEndDate];
};

const getLastMonthDateRange = () => {
  const currentDate = new Date();

  const lastMonthStartDate = new Date(currentDate);
  lastMonthStartDate.setMonth(lastMonthStartDate.getMonth() - 1);
  lastMonthStartDate.setDate(1);
  lastMonthStartDate.setHours(0, 0, 0, 0);

  const lastMonthEndDate = new Date(currentDate);
  lastMonthEndDate.setMonth(lastMonthEndDate.getMonth());
  lastMonthEndDate.setDate(0);
  lastMonthEndDate.setHours(23, 59, 59, 999);

  return [lastMonthStartDate, lastMonthEndDate];
};

const getCurrentMonthDateRange = () => {
  const currentDate = new Date();

  const currentMonthStartDate = new Date(currentDate);
  currentMonthStartDate.setDate(1);
  currentMonthStartDate.setHours(0, 0, 0, 0);

  const currentMonthEndDate = new Date(currentDate);

  return [currentMonthStartDate, currentMonthEndDate];
};

/**
 * Component
 */





const selectedRange = computed({
  get: () => props.value,
  set: (newValue) => {
    emit("input", newValue);
  },
});

const shortcuts = computed(() => {
  return props.isRange
    ? [
        {
          text: props.sidebarOptions[0],
          onClick() {
            return getCurrentDayDateRange();
          },
        },
        {
          text: props.sidebarOptions[1],
          onClick() {
            return getYesterdayDateRange();
          },
        },
        {
          text: props.sidebarOptions[2],
          onClick() {
            return getCurrentWeekDateRange();
          },
        },
        {
          text: props.sidebarOptions[3],
          onClick() {
            return getLastWeekDateRange();
          },
        },
        {
          text: props.sidebarOptions[4],
          onClick() {
            return getLast2WeeksDateRange();
          },
        },
        {
          text: props.sidebarOptions[5],
          onClick() {
            return getCurrentMonthDateRange();
          },
        },
        {
          text: props.sidebarOptions[6],
          onClick() {
            return getLastMonthDateRange();
          },
        },
      ]
    : [];
});

const lang = computed(() => {
  return {
    formatLocale: {
      firstDayOfWeek: 1,
      months: props.monthNames,
      weekdaysMin: props.weekDayNames,
    },
    monthFormat: "MMMM",
  };
});

const handleClearClick = () => {
  let newDateRange: DateInput = null;

  if (props.isRange) {
    newDateRange = [null, null];
  }

  selectedRange.value = newDateRange;
};

return { __sfc: true,getCurrentDayDateRange, getYesterdayDateRange, getCurrentWeekDateRange, getLastWeekDateRange, getLast2WeeksDateRange, getLastMonthDateRange, getCurrentMonthDateRange, props, emit, selectedRange, shortcuts, lang, handleClearClick, DatePicker, ButtonCommon }
}

})